import type { TenantCurrencyConversionType } from "@/actions/tenantCurrencyConversions.actions";

/**
 * Market rate as base-currency units per one selling-currency unit (1 USD = N IDR). This is the same Frankfurter
 * lookup the itinerary page uses to auto-fill its conversion rate; the server's `getMarketRate` uses the same pair.
 */
export const fetchMarketRate = async (selling: string, base: string, signal?: AbortSignal): Promise<number> => {
  const response = await fetch(`https://api.frankfurter.dev/v2/rate/${encodeURIComponent(selling)}/${encodeURIComponent(base)}`, { signal });
  if (!response.ok) throw new Error("Rate lookup failed");
  const rate = Number(((await response.json()) as { rate?: number }).rate);
  if (!Number.isFinite(rate) || rate <= 0) throw new Error("Invalid currency rate");
  return Number(rate.toFixed(6));
};

/** The tenant-configured rate for one unit of `from` expressed in `to`, using either stored direction. */
export const findTenantRate = (rates: TenantRateLike[], from: string, to: string): number | undefined => {
  if (from === to) return 1;
  const direct = effectiveTenantRate(rates.find((entry) => entry.baseCurrency === to && entry.conversionCurrency === from));
  if (direct) return direct;
  const inverse = effectiveTenantRate(rates.find((entry) => entry.baseCurrency === from && entry.conversionCurrency === to));
  return inverse ? 1 / inverse : undefined;
};

type TenantRateLike = Pick<TenantCurrencyConversionType, "baseCurrency" | "conversionCurrency" | "rate"> &
  Partial<Pick<TenantCurrencyConversionType, "effectiveRate" | "useLiveRate">>;

/** The rate quotes use for one conversion: live or fixed, minus its buffer (the server computes it as `effectiveRate`). */
const effectiveTenantRate = (entry?: TenantRateLike): number | undefined => {
  if (!entry) return undefined;
  if (entry.effectiveRate !== undefined) return Number(entry.effectiveRate) > 0 ? Number(entry.effectiveRate) : undefined;
  return Number(entry.rate) > 0 ? Number(entry.rate) : undefined;
};

/** Same, for the one conversion whose base is the costing currency, as the itinerary page needs it. */
export const configuredTenantRate = (rates: TenantRateLike[], base: string, selling: string): number | undefined =>
  effectiveTenantRate(rates.find((entry) => entry.baseCurrency === base && entry.conversionCurrency === selling));
